import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../hooks/useAuth';
import { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import SEO from '../components/SEO';

// Mini mock UI shown in the hero section
function AppPreview({ theme }: { theme: any }) {
  const files = [
    { icon: '🖼️', name: 'holiday-photos.zip', size: '142 MB', type: 'archive' },
    { icon: '📄', name: 'project-brief.pdf', size: '2.4 MB', type: 'pdf' },
    { icon: '🎵', name: 'soundtrack.mp3', size: '8.1 MB', type: 'audio' },
    { icon: '📊', name: 'budget-2026.xlsx', size: '540 KB', type: 'sheet' },
    { icon: '🎬', name: 'promo-video.mp4', size: '310 MB', type: 'video' },
    { icon: '📝', name: 'meeting-notes.docx', size: '98 KB', type: 'doc' },
  ];

  return (
    <div style={{
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${theme.colors.border}`,
      background: theme.colors.bgPrimary,
      display: 'flex',
      height: '380px',
      fontSize: '12px',
      userSelect: 'none',
    }}>
      {/* Sidebar */}
      <div style={{
        width: '140px',
        background: theme.colors.bgSecondary,
        borderRight: `1px solid ${theme.colors.border}`,
        padding: '16px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flexShrink: 0,
      }}>
        <div style={{
          fontWeight: 700,
          fontSize: '13px',
          background: theme.gradients.brand,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '12px',
          paddingLeft: '8px',
        }}>Trubonn</div>
        {[
          { icon: '🏠', label: 'Home', active: false },
          { icon: '📚', label: 'Library', active: true },
          { icon: '📤', label: 'Uploads', active: false },
          { icon: '🗑️', label: 'Trash', active: false },
          { icon: '⚙️', label: 'Settings', active: false },
        ].map(({ icon, label, active }) => (
          <div key={label} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '7px 8px',
            borderRadius: '6px',
            background: active ? `${theme.colors.brand}20` : 'transparent',
            color: active ? theme.colors.brand : theme.colors.textSecondary,
            borderLeft: active ? `2px solid ${theme.colors.brand}` : '2px solid transparent',
          }}>
            <span>{icon}</span>
            <span style={{ fontWeight: active ? 600 : 400 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: '16px', overflowY: 'hidden', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '14px' }}>My Library</span>
          <div style={{
            background: theme.colors.bgSecondary,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '6px',
            padding: '5px 10px',
            color: theme.colors.textSecondary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span>🔍</span>
            <span>Search files...</span>
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {[
            { label: 'Total Files', value: '128' },
            { label: 'Storage Used', value: '4.2 GB' },
            { label: 'This Week', value: '14' },
          ].map(({ label, value }) => (
            <div key={label} style={{
              background: theme.colors.bgSecondary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              padding: '10px 12px',
            }}>
              <div style={{ color: theme.colors.textSecondary, marginBottom: '4px', fontSize: '10px' }}>{label}</div>
              <div style={{ color: theme.colors.textPrimary, fontWeight: 700, fontSize: '15px' }}>{value}</div>
            </div>
          ))}
        </div>

        {/* File list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
          {files.map((file) => (
            <div key={file.name} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '7px 10px',
              borderRadius: '6px',
              background: theme.colors.bgSecondary,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <span style={{ fontSize: '16px' }}>{file.icon}</span>
              <span style={{ color: theme.colors.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
              <span style={{ color: theme.colors.textSecondary, flexShrink: 0 }}>{file.size}</span>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <span style={{ cursor: 'pointer', opacity: 0.6 }}>⬇️</span>
                <span style={{ cursor: 'pointer', opacity: 0.6 }}>🔗</span>
                <span style={{ cursor: 'pointer', opacity: 0.6 }}>⭐</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { isMobile } = useMediaQuery();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleGetStarted = () => {
    instance.loginRedirect({
      scopes: ['openid', 'profile'],
    });
  };

  const handleSignIn = () => {
    instance.loginRedirect({
      scopes: ['openid', 'profile'],
    });
  };

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bgPrimary,
      color: theme.colors.textPrimary,
    }}>
      <SEO
        title="Trubonn — Secure Cloud Storage for Australians"
        description="Trubonn is Australian-hosted cloud file storage with no data mining. Store, organise, and share your files securely. Australian Privacy Act compliant. Start free for 7 days."
        path="/"
      />
      {/* Navigation */}
      <nav style={{
        padding: isMobile ? '16px 20px' : '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        position: 'sticky',
        top: 0,
        background: theme.colors.bgPrimary,
        zIndex: 1000,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src="/logo.svg"
            alt="Trubonn Logo"
            style={{ height: isMobile ? '44px' : '70px', width: isMobile ? '120px' : '180px' }}
          />
        </div>

        {isMobile ? (
          /* Mobile nav — Get Started + hamburger */
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={handleGetStarted}
              style={{
                background: theme.gradients.brand,
                border: 'none',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Get Started
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                color: theme.colors.textPrimary,
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px 10px',
                borderRadius: '6px',
              }}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        ) : (
          /* Desktop nav */
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <button
              onClick={scrollToFeatures}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '16px',
                padding: '8px 16px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
            >
              Features
            </button>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '16px',
                padding: '8px 16px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/blog')}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '16px',
                padding: '8px 16px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
            >
              Blog
            </button>

            {/* Sign In button — for existing users */}
            <button
              onClick={handleSignIn}
              style={{
                background: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                color: theme.colors.textPrimary,
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.colors.brand;
                e.currentTarget.style.color = theme.colors.brand;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.colors.border;
                e.currentTarget.style.color = theme.colors.textPrimary;
              }}
            >
              Sign In
            </button>

            {/* Get Started button — for new users */}
            <button
              onClick={handleGetStarted}
              style={{
                background: theme.gradients.brand,
                border: 'none',
                color: '#fff',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(157, 78, 221, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Get Started
            </button>
          </div>
        )}
      </nav>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div style={{
          background: theme.colors.bgSecondary,
          borderBottom: `1px solid ${theme.colors.border}`,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 999,
        }}>
          <button
            onClick={scrollToFeatures}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '16px', padding: '12px', textAlign: 'left', borderRadius: '8px' }}
          >
            Features
          </button>
          <button
            onClick={() => { navigate('/pricing'); setMenuOpen(false); }}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '16px', padding: '12px', textAlign: 'left', borderRadius: '8px' }}
          >
            Pricing
          </button>
          <button
            onClick={() => { navigate('/blog'); setMenuOpen(false); }}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '16px', padding: '12px', textAlign: 'left', borderRadius: '8px' }}
          >
            Blog
          </button>
          <button
            onClick={handleSignIn}
            style={{ background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textPrimary, padding: '12px', borderRadius: '8px', fontSize: '15px', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
          >
            Sign In
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        textAlign: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <h1 style={{
          fontSize: isMobile ? '40px' : '64px',
          fontWeight: 800,
          marginBottom: '24px',
          lineHeight: 1.2,
          background: theme.gradients.brand,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          File storage, made easy.
        </h1>

        <p style={{
          fontSize: isMobile ? '16px' : '24px',
          color: theme.colors.textSecondary,
          marginBottom: '48px',
          maxWidth: '700px',
          margin: '0 auto 48px',
          lineHeight: 1.6,
        }}>
          Store, organize, and share your files securely in the cloud.
          Built on Microsoft Azure for enterprise-grade reliability.
        </p>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleGetStarted}
            style={{
              background: theme.gradients.brand,
              border: 'none',
              color: '#fff',
              padding: isMobile ? '14px 28px' : '16px 40px',
              borderRadius: '12px',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(157, 78, 221, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(157, 78, 221, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.3)';
            }}
          >
            Get Started →
          </button>

          <button
            onClick={scrollToFeatures}
            style={{
              background: 'transparent',
              border: `2px solid ${theme.colors.brand}`,
              color: theme.colors.brand,
              padding: isMobile ? '14px 28px' : '16px 40px',
              borderRadius: '12px',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${theme.colors.brand}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Learn More
          </button>
        </div>

        {/* Hero App Preview — hidden on mobile */}
        {!isMobile && (
          <div style={{
            marginTop: '80px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(157, 78, 221, 0.2), 0 0 0 1px rgba(157, 78, 221, 0.1)',
            border: `1px solid ${theme.colors.border}`,
            background: theme.colors.bgSecondary,
            padding: '24px',
          }}>
            <AppPreview theme={theme} />
          </div>
        )}
      </section>

      {/* Trust Bar */}
      <section style={{
        padding: isMobile ? '24px 20px' : '28px 40px',
        background: theme.colors.bgSecondary,
        borderTop: `1px solid ${theme.colors.border}`,
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '16px' : '48px',
        }}>
          {[
            { icon: '🔒', text: 'AES-256 Encrypted' },
            { icon: '🇦🇺', text: 'Australian Hosted' },
            { icon: '🚫', text: 'No Data Mining' },
            { icon: '✅', text: '7-Day Free Trial' },
            { icon: '↩️', text: 'Cancel Anytime' },
          ].map(({ icon, text }) => (
            <div key={text} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ fontSize: '18px' }}>{icon}</span>
              <span style={{
                color: theme.colors.textSecondary,
                fontSize: '14px',
                fontWeight: 500,
              }}>{text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: theme.colors.bgSecondary,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Everything you need
          </h2>

          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: theme.colors.textSecondary,
            textAlign: 'center',
            marginBottom: '80px',
            maxWidth: '600px',
            margin: '0 auto 80px',
          }}>
            Powerful features to manage your files efficiently
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
          }}>
            {[
              { icon: '🔒', title: 'Secure Storage', desc: 'Enterprise-grade security with AES-256 encryption at rest and TLS in transit. Your files are protected around the clock.' },
              { icon: '📁', title: 'Smart Organisation', desc: 'Organise with folders, tags, and favourites. Find your files instantly with powerful search.' },
              { icon: '🔗', title: 'Easy Sharing', desc: 'Generate secure 24-hour sharing links. Share files safely without compromising security.' },
              { icon: '⚡', title: 'File Management', desc: 'Upload, rename, delete with ease. 30-day trash recovery keeps your files safe from accidents.' },
              { icon: '📱', title: 'Modern Interface', desc: 'Beautiful dark theme design that works seamlessly on desktop, tablet, and mobile devices.' },
              { icon: '🚀', title: 'Fast & Reliable', desc: 'Built on enterprise cloud infrastructure for lightning-fast uploads and 99.9% uptime.' },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                style={{
                  padding: '32px',
                  background: theme.colors.bgPrimary,
                  borderRadius: '16px',
                  border: `1px solid ${theme.colors.border}`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(157, 78, 221, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>{icon}</div>
                <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px', color: theme.colors.textPrimary }}>{title}</h3>
                <p style={{ color: theme.colors.textSecondary, lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Australian Section */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: theme.colors.bgPrimary,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🇦🇺</div>
            <h2 style={{
              fontSize: isMobile ? '32px' : '48px',
              fontWeight: 700,
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Built for Australians
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: theme.colors.textSecondary,
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}>
              Unlike overseas providers, Trubonn is designed with Australian privacy laws and values in mind.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '24px',
          }}>
            {[
              {
                icon: '🏠',
                title: 'Data Stays in Australia',
                desc: 'Your files are stored on Australian servers and never leave the country. No overseas data centres, no exceptions.',
              },
              {
                icon: '🛡️',
                title: 'Privacy Act Compliant',
                desc: 'Fully compliant with the Australian Privacy Act 1988 and Australian Privacy Principles. Your rights are protected.',
              },
              {
                icon: '🚫',
                title: 'No Data Mining',
                desc: 'We never analyse, sell, or share your files. Your data is yours — we use it solely to provide the storage service.',
              },
              {
                icon: '💬',
                title: 'Australian Support',
                desc: 'Get help from our Australian-based support team. Real people, same timezone, no overseas call centres.',
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                style={{
                  padding: '28px',
                  background: theme.colors.bgSecondary,
                  borderRadius: '16px',
                  border: `1px solid ${theme.colors.brand}30`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 12px 32px rgba(157, 78, 221, 0.15)`;
                  e.currentTarget.style.borderColor = `${theme.colors.brand}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = `${theme.colors.brand}30`;
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{icon}</div>
                <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '10px', color: theme.colors.textPrimary }}>{title}</h3>
                <p style={{ color: theme.colors.textSecondary, lineHeight: 1.6, margin: 0, fontSize: '15px' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: theme.colors.bgPrimary,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Getting started is easy
          </h2>

          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: theme.colors.textSecondary,
            textAlign: 'center',
            marginBottom: '80px',
          }}>
            Start storing your files in minutes
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '48px',
            textAlign: 'center',
          }}>
            {[
              { step: '1', title: 'Sign Up Free', desc: 'Create your account and start your 7-day free trial. No charge until Day 8.' },
              { step: '2', title: 'Upload Files', desc: 'Drag and drop your files or browse to upload. Simple as that.' },
              { step: '3', title: 'Organise & Share', desc: 'Use folders and tags to organise. Share with secure links.' },
            ].map(({ step, title, desc }) => (
              <div key={step}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: theme.gradients.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '36px',
                  fontWeight: 700,
                  color: '#fff',
                  boxShadow: '0 4px 20px rgba(157, 78, 221, 0.4)',
                }}>
                  {step}
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px', color: theme.colors.textPrimary }}>{title}</h3>
                <p style={{ color: theme.colors.textSecondary, lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: theme.colors.bgSecondary,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            What our users say
          </h2>
          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: theme.colors.textSecondary,
            textAlign: 'center',
            marginBottom: isMobile ? '40px' : '64px',
            maxWidth: '600px',
            margin: '0 auto',
            paddingBottom: isMobile ? '40px' : '64px',
          }}>
            Australians who care about where their data lives
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
          }}>
            {[
              {
                quote: 'Finally a cloud storage service that keeps my files in Australia. Setup took less than 5 minutes and I love knowing my data stays here.',
                name: 'Sarah M.',
                location: 'Melbourne, VIC',
                initials: 'SM',
              },
              {
                quote: 'After the Optus breach I became very conscious of where my data is stored. Trubonn gives me peace of mind that my files never leave Australia.',
                name: 'James T.',
                location: 'Sydney, NSW',
                initials: 'JT',
              },
              {
                quote: 'Simple, clean and does exactly what it says. I store all my work documents here and love that there\'s no data mining or ads.',
                name: 'Priya K.',
                location: 'Brisbane, QLD',
                initials: 'PK',
              },
            ].map(({ quote, name, location, initials }) => (
              <div
                key={name}
                style={{
                  padding: '32px',
                  background: theme.colors.bgPrimary,
                  borderRadius: '16px',
                  border: `1px solid ${theme.colors.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                {/* Stars */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[1,2,3,4,5].map((s) => (
                    <span key={s} style={{ color: '#F59E0B', fontSize: '18px' }}>★</span>
                  ))}
                </div>

                {/* Quote */}
                <p style={{
                  color: theme.colors.textSecondary,
                  lineHeight: 1.7,
                  fontSize: '15px',
                  margin: 0,
                  flex: 1,
                  fontStyle: 'italic',
                }}>
                  "{quote}"
                </p>

                {/* Author */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: theme.gradients.brand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#fff',
                    flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div>
                    <div style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '15px' }}>{name}</div>
                    <div style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>{location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: theme.colors.bgSecondary,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: 700,
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Ready to get started?
          </h2>

          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: theme.colors.textSecondary,
            marginBottom: '48px',
            lineHeight: 1.6,
          }}>
            Join users who trust Trubonn to store and manage their files securely in the cloud.
          </p>

          <button
            onClick={handleGetStarted}
            style={{
              background: theme.gradients.brand,
              border: 'none',
              color: '#fff',
              padding: isMobile ? '16px 36px' : '20px 48px',
              borderRadius: '12px',
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(157, 78, 221, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(157, 78, 221, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.3)';
            }}
          >
            Get Started →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? '32px 20px' : '40px',
        textAlign: 'center',
        borderTop: `1px solid ${theme.colors.border}`,
        background: theme.colors.bgPrimary,
      }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>
          © 2026 Trubonn Systems. All rights reserved.
        </p>
        <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/privacy" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Privacy Policy
          </a>
          <a href="/terms" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Terms of Service
          </a>
          <a href="/blog" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Blog
          </a>
          <a href="/contact" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}
